import type { Uuid } from "@/domains/global/identifiers";
import type { AppStore } from "@/store/AppStore";
import {
  AudioRecordingUploadStatus,
  type AudioRecordingObservable,
} from "@/store/audio-recordings";

/**
 * UI state for the voice source chip (background upload progress).
 *
 * This is deliberately independent of the main pipeline snapshot: the chip
 * tracks happy-path background uploads that run alongside transform, not the
 * main pipeline stage walk.
 *
 * Invariant:
 * - the in-memory uploading store is the source of truth for live progress
 * - the durable recording row is the source of truth for terminal status
 *
 * Those two surfaces can overlap briefly during optimistic transitions, so
 * this projection prefers live progress while expecting both surfaces to
 * describe the same upload job.
 */
export enum AudioSourceUploadStateValue {
  Idle = "idle",
  Uploading = "uploading",
  Error = "error",
  Skipped = "skipped",
  Completed = "completed",
}

export type AudioSourceUploadState = {
  state: AudioSourceUploadStateValue;
  progressPercent: number | null;
  errorMessage: string | null;
  isUploading: boolean;
};

const IDLE_STATE: AudioSourceUploadState = {
  state: AudioSourceUploadStateValue.Idle,
  progressPercent: null,
  errorMessage: null,
  isUploading: false,
};

export function getSourceUploadState({
  store,
  audioRecordingId,
}: {
  store: AppStore;
  audioRecordingId: Uuid | null;
}): AudioSourceUploadState {
  if (!audioRecordingId) return IDLE_STATE;

  const uploadState = store.audio.uploading.getProgress({ audioRecordingId });
  const recordingObservable: AudioRecordingObservable | null =
    store.audioRecordings.get(audioRecordingId) ?? null;

  const progressPercent =
    uploadState?.uploadProgressPercent ?? recordingObservable?.uploadProgressPercent ?? null;
  const errorMessage = uploadState?.errorMessage ?? recordingObservable?.uploadErrorMessage ?? null;
  const uploadStatus = recordingObservable?.uploadStatus;
  const isUploading =
    uploadState?.isUploading === true || uploadStatus === AudioRecordingUploadStatus.IN_PROGRESS;

  if (isUploading) {
    return {
      state: AudioSourceUploadStateValue.Uploading,
      progressPercent,
      errorMessage: null,
      isUploading: true,
    };
  }

  if (uploadStatus === AudioRecordingUploadStatus.ERROR) {
    return {
      state: AudioSourceUploadStateValue.Error,
      progressPercent,
      errorMessage: errorMessage ?? "Upload failed",
      isUploading: false,
    };
  }

  if (uploadStatus === AudioRecordingUploadStatus.SKIPPED) {
    return {
      state: AudioSourceUploadStateValue.Skipped,
      progressPercent,
      errorMessage: "Upload skipped",
      isUploading: false,
    };
  }

  if (uploadStatus === AudioRecordingUploadStatus.COMPLETED) {
    return {
      state: AudioSourceUploadStateValue.Completed,
      progressPercent,
      errorMessage: null,
      isUploading: false,
    };
  }

  return {
    state: AudioSourceUploadStateValue.Idle,
    progressPercent,
    errorMessage: null,
    isUploading: false,
  };
}
